/**
 * useCanonicalHead — 색인 대상 라우트에 canonical `<link>` 를 한 곳에서 주입한다.
 *
 * 검색엔진은 `http://`·`https://`·`www.` 유무를 각각 다른 URL 로 취급한다. 리다이렉트가 걸려 있어도
 * 구글은 자체 판단으로 정식(canonical) URL 을 고르므로, 정식 주소를 명시해 색인 신호가 한 URL 로
 * 모이게 한다(파비콘·사이트링크 등 부가 표시도 canonical 기준으로 수집된다).
 *
 * ⚠️ 페이지마다 `useHead({ link: [...] })` 를 흩뿌리지 않는다 — 라우트가 늘 때마다 누락되고,
 * 절대 URL 하드코딩이 번진다. App.vue 에서 한 번만 호출해 라우트 기반으로 계산한다.
 *
 * 대상 판정은 `config/seoRoutes.ts` 의 `isIndexablePath`(색인 정책 SSoT) — 프리렌더·noindex 와 같은 기준이다.
 * 제외: 데이터 기준 canonical 을 스스로 내는 라우트(가이드 — `SELF_CANONICAL_ROUTE_NAMES`, 중복 방지)와
 * 비색인 라우트(마이페이지·결제·리포트 상세 등 — canonical 이 의미 없고 noindex 대상), 그리고 색인 패턴이지만 페이지가
 * 데이터 없음을 알린 경로(`usePageNotFound` — soft 404 에 canonical 을 내지 않는다).
 */
import { useHead } from '@unhead/vue';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { canonicalUrl } from '#/config/brand';
import { isIndexablePath, SELF_CANONICAL_ROUTE_NAMES } from '#/config/seoRoutes';
import { usePageStatusStore } from '#/stores/pageStatus';

/**
 * 현재 라우트의 canonical URL 을 head 에 주입한다(대상 아니면 아무것도 넣지 않음).
 *
 * 쿼리스트링·해시는 제외한다 — 같은 문서의 필터·앵커 변형이 별개 URL 로 색인되지 않게 한다.
 */
export function useCanonicalHead(): void {
  const route = useRoute();
  const pageStatus = usePageStatusStore();
  /**
   * 정식 절대 URL(대상 아니면 null).
   *
   * 판정은 `route.path`(슬래시 유무 그대로) 로 하되, 발행하는 값은 `canonicalUrl` 로 정규화한다 —
   * 실제 200 을 내는 형태가 trailing slash 이므로, 그대로 쓰면 canonical 이 자기 자신의
   * 리다이렉트 대상이 되어 색인에서 빠진다(근거: brand.ts `canonicalUrl`).
   */
  const canonical = computed(() => {
    if (SELF_CANONICAL_ROUTE_NAMES.has(route.name as string)) return null;
    if (pageStatus.isNotFound(route.path)) return null;
    return isIndexablePath(route.path) ? canonicalUrl(route.path) : null;
  });

  useHead({
    link: () => (canonical.value ? [{ rel: 'canonical', href: canonical.value }] : []),
  });
}
